import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FotosPerfil {
  /** Foto por correo (en minúsculas). */
  porEmail: (email: string | null | undefined) => string | null;
  /** Foto por `id_persona` del usuario. */
  porPersona: (idPersona: number | null | undefined) => string | null;
}

/**
 * Fotos de perfil (`usuarios.foto_perfil_url`) de varias personas en una sola consulta, por
 * correo o por `id_persona`. Para `AvatarPersona`. Quien no tiene usuario, o no se puede leer por
 * RLS (fuera de staff), sale sin foto y el avatar muestra iniciales.
 */
export function useFotosPerfil({
  emails = [],
  idsPersona = [],
}: {
  emails?: (string | null | undefined)[];
  idsPersona?: (number | null | undefined)[];
}): FotosPerfil {
  const correos = [...new Set(emails.map((e) => (e ?? "").trim().toLowerCase()).filter(Boolean))].sort();
  const ids = [...new Set(idsPersona.filter((i): i is number => typeof i === "number" && i > 0))].sort((a, b) => a - b);

  const { data } = useQuery({
    queryKey: ["fotos_perfil", correos, ids],
    enabled: correos.length > 0 || ids.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const filas: { email: string; id_persona: number | null; foto_perfil_url: string | null }[] = [];
      // Bloques de 200 para no pasarse del largo de URL de PostgREST.
      for (let i = 0; i < correos.length; i += 200) {
        const { data: d } = await supabase
          .from("usuarios")
          .select("email, id_persona, foto_perfil_url")
          .in("email", correos.slice(i, i + 200))
          .not("foto_perfil_url", "is", null);
        filas.push(...((d ?? []) as typeof filas));
      }
      for (let i = 0; i < ids.length; i += 200) {
        const { data: d } = await supabase
          .from("usuarios")
          .select("email, id_persona, foto_perfil_url")
          .in("id_persona", ids.slice(i, i + 200))
          .not("foto_perfil_url", "is", null);
        filas.push(...((d ?? []) as typeof filas));
      }
      return filas;
    },
  });

  const porEmailMap = new Map<string, string>();
  const porPersonaMap = new Map<number, string>();
  for (const f of data ?? []) {
    if (!f.foto_perfil_url) continue;
    porEmailMap.set(f.email.toLowerCase(), f.foto_perfil_url);
    if (f.id_persona) porPersonaMap.set(f.id_persona, f.foto_perfil_url);
  }
  return {
    porEmail: (e) => (e ? porEmailMap.get(e.trim().toLowerCase()) ?? null : null),
    porPersona: (id) => (id ? porPersonaMap.get(id) ?? null : null),
  };
}
